import { alpha, Box, Typography } from '@mui/material';
import React from 'react';

interface StaffMemberViewProps {
    name: string;
    pictureUrl: string;
    description: React.ReactElement;
    reverse?: boolean;
}

const StaffMemberView: React.FC<StaffMemberViewProps> = ({
    name,
    pictureUrl,
    description,
    reverse = false,
}) => {
    return (
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 1, sm: 2 } }}>
            <Box
                sx={(theme) => ({
                    display: 'flex',
                    flexDirection: { xs: 'column', md: reverse ? 'row-reverse' : 'row' },
                    alignItems: { xs: 'stretch', md: 'center' },
                    gap: { xs: 3, md: 5 },
                    p: { xs: 3, sm: 4 },
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 4,
                        height: '100%',
                        backgroundColor: theme.palette.primary.main,
                        ...(reverse && { left: 'auto', right: 0 }),
                    },
                })}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        width: { xs: '100%', md: 380 },
                        maxWidth: { xs: '100%', md: 380 },
                        alignSelf: { xs: 'center', md: 'stretch' },
                    }}
                >
                    <Box
                        component="img"
                        src={pictureUrl}
                        alt={name}
                        sx={{
                            width: '100%',
                            height: { xs: 360, sm: 400, md: 460 },
                            maxHeight: { xs: 420, md: 540 },
                            objectFit: 'cover',
                            objectPosition: 'top center',
                            borderRadius: 2,
                            boxShadow: '0 12px 24px rgba(0,0,0,0.25)',
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        fontWeight={700}
                        color="primary.main"
                        mb={2}
                        sx={{ letterSpacing: '-0.02em' }}
                    >
                        {name}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            '& .MuiTypography-root': {
                                color: 'text2.primary',
                                lineHeight: 1.7,
                            },
                        }}
                    >
                        {description}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default StaffMemberView;
